import { useState, useEffect } from 'react';
import { getQuoteRequests, updateQuoteRequestStatus } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FileSearch, Clock, CheckCircle, XCircle, Loader2, ExternalLink, User, Building } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

export const QuoteRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await getQuoteRequests();
      setRequests(res.data);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await updateQuoteRequestStatus(quoteId, newStatus);
      toast.success('ステータスを更新しました');
      fetchRequests();
    } catch (error) {
      toast.error('更新に失敗しました');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />確認中</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800"><Loader2 className="h-3 w-3 mr-1" />対応中</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />完了</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />キャンセル</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const filteredRequests = filter === 'all' 
    ? requests 
    : requests.filter(r => r.status === filter);

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="quote-requests-page">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <h1 className="page-title">見積もり依頼</h1>
          {pendingCount > 0 && (
            <Badge className="bg-red-500 text-white">{pendingCount}件 未対応</Badge>
          )}
        </div>
        <p className="text-slate-500 text-sm mt-1">顧客からの見積もり依頼を確認・管理</p>
      </div>

      {/* フィルター */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          すべて ({requests.length})
        </Button>
        <Button
          variant={filter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('pending')}
          className={filter === 'pending' ? 'bg-yellow-600' : ''}
        >
          確認中 ({requests.filter(r => r.status === 'pending').length})
        </Button>
        <Button
          variant={filter === 'in_progress' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('in_progress')}
          className={filter === 'in_progress' ? 'bg-blue-600' : ''}
        >
          対応中 ({requests.filter(r => r.status === 'in_progress').length})
        </Button>
        <Button
          variant={filter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('completed')}
          className={filter === 'completed' ? 'bg-green-600' : ''}
        >
          完了 ({requests.filter(r => r.status === 'completed').length})
        </Button>
      </div>

      {/* 依頼一覧 */}
      {filteredRequests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileSearch className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">見積もり依頼がありません</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((req) => (
            <Card key={req.id} className={`border-l-4 ${
              req.status === 'pending' ? 'border-l-yellow-500' :
              req.status === 'in_progress' ? 'border-l-blue-500' :
              req.status === 'completed' ? 'border-l-green-500' :
              'border-l-slate-300'
            }`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  {/* 左側: 依頼内容 */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-lg text-slate-900">{req.product_name}</h3>
                        <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                          <Building className="h-4 w-4" />
                          <span>{req.customer_name}</span>
                          {req.customer_code && (
                            <span className="text-slate-400">({req.customer_code})</span>
                          )}
                        </div>
                      </div>
                      {getStatusBadge(req.status)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {req.temperature_type && (
                        <div>
                          <span className="text-slate-500">温度帯:</span>
                          <span className="ml-1 font-medium">{req.temperature_type}</span>
                        </div>
                      )}
                      {req.desired_size && (
                        <div>
                          <span className="text-slate-500">サイズ:</span>
                          <span className="ml-1 font-medium">{req.desired_size}</span>
                        </div>
                      )}
                      {req.desired_price && (
                        <div>
                          <span className="text-slate-500">希望価格:</span>
                          <span className="ml-1 font-medium">{req.desired_price}</span>
                        </div>
                      )}
                      {req.usage && (
                        <div>
                          <span className="text-slate-500">用途:</span>
                          <span className="ml-1 font-medium">{req.usage}</span>
                        </div>
                      )}
                      {req.monthly_volume && (
                        <div>
                          <span className="text-slate-500">月間使用量:</span>
                          <span className="ml-1 font-medium">{req.monthly_volume}</span>
                        </div>
                      )}
                    </div>

                    {req.other_requests && (
                      <div className="bg-slate-50 p-2 rounded text-sm">
                        <span className="text-slate-500">その他:</span>
                        <p className="mt-1">{req.other_requests}</p>
                      </div>
                    )}

                    {req.reference_url && (
                      <a 
                        href={req.reference_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-blue-600 hover:underline"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        参考URL
                      </a>
                    )}

                    <p className="text-xs text-slate-400">
                      依頼日時: {format(new Date(req.created_at), 'yyyy/MM/dd HH:mm', { locale: ja })}
                    </p>
                  </div>

                  {/* 右側: ステータス変更 */}
                  <div className="flex flex-col gap-2 min-w-[150px]">
                    <label className="text-xs text-slate-500">ステータス変更</label>
                    <Select
                      value={req.status}
                      onValueChange={(value) => handleStatusChange(req.id, value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">確認中</SelectItem>
                        <SelectItem value="in_progress">対応中</SelectItem>
                        <SelectItem value="completed">完了</SelectItem>
                        <SelectItem value="cancelled">キャンセル</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
